import React from 'react'
import { differenceInCalendarDays } from 'date-fns'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { navigateToStaticDemo } from 'utils/staticDemo'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import { selectActualFeatureFlags } from 'containers/App/selectors'

import Button from 'components/lib/DesignSystem/Button'
import Icon from 'components/lib/Icon'

const PHONE_BREAKPOINT = 750

/**
 * Banner styled components - heights are now determined by content + padding
 * No more hardcoded heights or position: fixed
 */

const SilentModeContent = styled.div.attrs(() => ({
  id: 'global-banner-silent-mode',
}))`
  color: white;
  font-weight: 700;
  font-size: 12px;
  padding: 6px 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: red;
`

const bannerIconContainerStyles = `
  .icon-container {
    width: 37px;
    height: 37px;
    margin-right: 18px;
    border-radius: 37px;
    background: var(--color-elevated-mark);
    display: flex;
    align-items: center;
    justify-content: center;
    span {
      padding: 8.5px;
    }
  }
`

const DemoModeContent = styled.div.attrs(() => ({
  id: 'global-banner-demo-mode',
}))`
  width: 100%;
  padding: 11px 0;
  background: var(--global-banner-surface-bg);
  box-shadow: var(--shadow-1);
  color: var(--color-text);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  ${bannerIconContainerStyles}
  .banner-text {
    font-weight: 600;
  }
  #demo-mode-banner-title {
    font-weight: 600;
  }
  #demo-mode-banner-description {
    color: var(--sub-text-color);
  }
`

const StaticDemoContent = styled.div.attrs(() => ({
  id: 'global-banner-static-demo',
}))`
  width: 100%;
  padding: 11px 0;
  background: var(--global-banner-promo-bg);
  box-shadow: var(--shadow-1);
  color: var(--color-text);
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  ${bannerIconContainerStyles}
  .banner-text {
    font-weight: 600;
  }
  a {
    color: var(--link-text-default);
    &:hover {
      color: var(--link-text-hover);
    }
  }
`

const DemoBannerContent = ({ mailboxesSyncing, handleSync }) => (
  <DemoModeContent>
    <div className="icon-container">
      <Icon imageName="space_astronaut_alternate" />
    </div>
    {mailboxesSyncing ? (
      <div className="banner-text">Syncing mailboxes...</div>
    ) : (
      <>
        <div className="tal">
          <div id="demo-mode-banner-title">You're in Demo Mode viewing sample data</div>
          <div id="demo-mode-banner-description">
            Explore IRONSCALES until you're ready to sync your mailboxes.
          </div>
        </div>
        <div style={{ marginLeft: '42px' }}>
          <Button id="demo-syncMailboxes-button" text="Sync my mailboxes" onClick={handleSync} />
        </div>
      </>
    )}
  </DemoModeContent>
)

const StaticDemoBannerContent = ({ handleDisableDemoMode }) => (
  <StaticDemoContent>
    <div className="icon-container">
      <Icon imageName="space_astronaut_alternate" fill="var(--primary)" />
    </div>
    <div className="banner-text">You're in Demo Mode viewing sample data</div>
    <div style={{ marginLeft: '28px' }}>
      <Button
        id="static-demo-button"
        styleType="secondary"
        text="Disable demo mode"
        onClick={handleDisableDemoMode}
      />
    </div>
  </StaticDemoContent>
)

const StarterFreeTrialBannerContent = ({ daysLeftToExpire, data }) => {
  const upgradeUrl = `https://www.ironscales.com/upgrade?tenant_id_cnc=${data.id}&tenant_domain_cnc=${data.domain}&partner_cnc=${data.isPartner}&enabled_mailboxes_cnc=${data.mailboxEnabled}&license_limit_cnc=${data.mailboxLimit}&email=${data.user.email}`
  return (
    <StaticDemoContent>
      <div className="icon-container">
        <Icon imageName="space_astronaut_alternate" fill="var(--primary)" />
      </div>
      <div className="banner-text">
        You're enjoying a free upgrade to IRONSCALES Protect! {daysLeftToExpire + 1} days left to
        experience enhanced email security.{' '}
        <a
          id="Global_Banner_Link_To_Upgrade"
          href={upgradeUrl}
          rel="noopener noreferrer"
          target="_blank"
        >
          Learn more & upgrade here.
        </a>
      </div>
    </StaticDemoContent>
  )
}

/**
 * GlobalBanner - Returns only banner content, no children wrapping
 *
 * This component is designed to be used with AppLayout which handles
 * the positioning. The banner content will be placed in the header area
 * and its height is determined automatically by CSS.
 *
 * @returns {React.ReactNode|null} Banner content or null if no banner should show
 */
const GlobalBanner = ({ data, showDemoBanner, mailboxesSyncing, history }) => {
  const ff = useSelector(selectActualFeatureFlags)
  const IS_SILENT_MODE = ff.SILENT_MODE_FF
  const IS_DEMO_MODE = showDemoBanner && window.innerWidth > PHONE_BREAKPOINT
  const IS_STATIC_DEMO_MODE = data?.isStaticSalesDemo && window.innerWidth > PHONE_BREAKPOINT

  const isMspDashboard = matchURL(URL_CONST.partners.self)
  const isStarterFreeTrial = ff.FREE_IS_PROTECT
  const licenseExpiryDate = data?.notifications?.license_expiery
    ? new Date(data.notifications.license_expiery)
    : null

  const handleSync = () => history.push(URL_CONST.mailboxes.upload)
  const handleDisableDemoMode = () => {
    // Full navigation so Django CreateStaticDemo runs (SPA push + reload can
    // stay on the shell). Partners land on /partners/; others on /dashboard/.
    navigateToStaticDemo()
  }

  // Priority order: Free Trial > Silent Mode > Demo Mode > Static Demo
  if (isStarterFreeTrial && data && licenseExpiryDate && data.user?.email) {
    return (
      <StarterFreeTrialBannerContent
        daysLeftToExpire={differenceInCalendarDays(licenseExpiryDate, new Date())}
        data={data}
      />
    )
  }
  if (isStarterFreeTrial) {
    return null
  }

  if (IS_SILENT_MODE && !isMspDashboard) {
    return <SilentModeContent>Running in Silent Mode - No mitigation will occur</SilentModeContent>
  }

  if (IS_DEMO_MODE && !isMspDashboard) {
    return <DemoBannerContent mailboxesSyncing={mailboxesSyncing} handleSync={handleSync} />
  }

  if (IS_STATIC_DEMO_MODE) {
    return <StaticDemoBannerContent handleDisableDemoMode={handleDisableDemoMode} />
  }

  return null
}

export default GlobalBanner
